import React, { useEffect, useState, useMemo, useRef } from "react";
import { keyframes } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import StrongDetail from "./StrongDetail";
import apiFetch from "../utils/apiFetch";

// Parpadeo del versículo al que se llega desde la búsqueda.
// El halo se pinta con box-shadow (fuera del border-box) para no alterar el layout.
const blinkLight = keyframes`
  0%, 100% { background-color: rgba(255, 193, 7, 0);    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0); }
  50%      { background-color: rgba(255, 193, 7, 0.55); box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.55); }
`;

const blinkDark = keyframes`
  0%, 100% { background-color: rgba(255, 213, 79, 0);    box-shadow: 0 0 0 4px rgba(255, 213, 79, 0); }
  50%      { background-color: rgba(255, 213, 79, 0.32); box-shadow: 0 0 0 4px rgba(255, 213, 79, 0.32); }
`;

const BLINK_MS = 1000;   // duración de un parpadeo
const BLINK_COUNT = 4;  // veces que parpadea

// Marca el popper del tooltip para poder distinguir un tap dentro de él de uno fuera
const KEYWORD_TOOLTIP_CLASS = "keyword-tooltip";

// Desplaza la ventana hasta dejar el versículo completo a la vista.
// Si el versículo cabe en el viewport lo centra; si no, lo alinea arriba
// para que al menos empiece a leerse desde el principio.
const scrollVerseIntoView = (el) => {
  const rect = el.getBoundingClientRect();
  const absoluteTop = rect.top + window.scrollY;
  const margin = 24;
  const viewport = window.innerHeight;
  const top = rect.height >= viewport - margin * 2
    ? absoluteTop - margin
    : absoluteTop - (viewport - rect.height) / 2;

  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
};

export default function BibleChapter({ book, chapter, highlightVerse }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Estado para controlar qué strongNumber está siendo hoverizado
  const [hoveredStrong, setHoveredStrong] = useState(null);
  // Estado para el modal de detalle Strong
  const [openStrongModal, setOpenStrongModal] = useState(false);
  const [selectedStrongInfo, setSelectedStrongInfo] = useState(null);
  // Versículo que está parpadeando ahora mismo (null = ninguno)
  const [blinkVerse, setBlinkVerse] = useState(null);
  // Nodos DOM de cada versículo, indexados por verseNumber
  const verseRefs = useRef(new Map());

  // En dispositivos sin hover (móvil/tablet) el tooltip por defecto de MUI exige
  // pulsación larga y se cierra al levantar el dedo, así que el botón "Ver detalle"
  // queda inalcanzable. Ahí lo controlamos nosotros: se abre con un tap y se
  // mantiene abierto hasta que se toca fuera o se vuelve a tocar la palabra.
  const [isTouch, setIsTouch] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(hover: none)").matches
  );
  // id del keyword cuyo tooltip está abierto por tap (null = ninguno)
  const [openKeywordId, setOpenKeywordId] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(hover: none)");
    const onChange = (ev) => setIsTouch(ev.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Cerrar el tooltip al tocar fuera. Los taps sobre el propio tooltip (p. ej. el
  // botón "Ver detalle") o sobre otra palabra clave no cuentan como "fuera".
  useEffect(() => {
    if (!isTouch || !openKeywordId) return;

    const onPointerDown = (ev) => {
      const t = ev.target;
      if (t && typeof t.closest === "function"
        && (t.closest(`.${KEYWORD_TOOLTIP_CLASS}`) || t.closest("[data-keyword-anchor]"))) return;
      setOpenKeywordId(null);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isTouch, openKeywordId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    verseRefs.current.clear();

    apiFetch(`/api/bible/chapter/${book}/${chapter}`)
      .then((res) => {
        if (!res.ok) throw new Error("No se pudo cargar el capítulo");
        return res.json();
      })
      .then((json) => {
        // Pequeño retraso para ver el efecto de fade
        setTimeout(() => {
          setData(json);
          setLoading(false);
        }, 400);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [book, chapter]);

  // Al llegar desde la búsqueda: desplazarse al versículo y hacerlo parpadear.
  // `highlightVerse` es un objeto nuevo en cada clic de "Ir", así que este efecto
  // se vuelve a disparar aunque se pida el mismo versículo dos veces seguidas.
  useEffect(() => {
    if (loading || !data || !highlightVerse) return;

    const target = Number(highlightVerse.verseNumber);
    if (!target) return;

    // Cortar cualquier parpadeo previo para que la animación reinicie de cero
    setBlinkVerse(null);

    let cancelled = false;
    let timer = null;

    // Esperar al siguiente frame: el Fade ya montó los versículos y las
    // posiciones que mide scrollVerseIntoView son las definitivas.
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;

      const el = verseRefs.current.get(target);
      if (!el) return;

      scrollVerseIntoView(el);
      setBlinkVerse(target);
      timer = setTimeout(() => {
        if (!cancelled) setBlinkVerse(null);
      }, BLINK_MS * BLINK_COUNT);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
    };
  }, [data, loading, highlightVerse]);


  // Calcular los 4 keywords más repetidos por strongNumber
  const topKeywords = useMemo(() => {
    if (!data || !data.verses) return [];
    const counts = {};
    data.verses.forEach((v) => {
      (v.keywords || []).forEach((k) => {
        const sn = (k.strongNumber || "").toString();
        if (!sn) return;
        // No contar el strongNumber H1961
        if (sn === "H1961") return;
        // Solo contar si sourceTransliteration no es nulo/empty
        if (!k.sourceTransliteration) return;
        if (!counts[sn]) {
          counts[sn] = {
            strongNumber: sn,
            sourceTransliteration: k.sourceTransliteration || "",
              sourceMeaning: k.sourceMeaning || "",
            count: 0,
          };
        }
        counts[sn].count += 1;
        // preferir sourceTransliteration no vacío
        if (!counts[sn].sourceTransliteration && k.sourceTransliteration) {
          counts[sn].sourceTransliteration = k.sourceTransliteration;
        }
      });
    });

    return Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 7);
  }, [data]);


  // Calcular los 4 compoundKeywords más repetidos por strongNumber
    const topCompoundKeywords = useMemo(() => {
      if (!data || !data.verses) return [];
      const counts = {};
      data.verses.forEach((v) => {
        (v.keywords || []).forEach((k) => {
          const sn = (k.strongNumber || "").toString();
          if (!sn) return;
          if (sn === "G5547 G2424") return;
          if (sn === "G2424 G5547") return;
          // Solo contar si compoundTransliteration no es nulo/empty
          if (!k.compoundTransliteration) return;
          if (!counts[sn]) {
            counts[sn] = {
              strongNumber: sn,
              compoundTransliteration: k.compoundTransliteration || "",
              compoundMeaning: k.compoundMeaning || "",
              count: 0,
            };
          }
          counts[sn].count += 1;
          // preferir compoundTransliteration no vacío
          if (!counts[sn].compoundTransliteration && k.compoundTransliteration) {
            counts[sn].compoundTransliteration = k.compoundTransliteration;
          }
        });
      });

      return Object.values(counts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 7);
    }, [data]);

  const openStrong = (wordInfo) => {
    setSelectedStrongInfo(wordInfo);
    setOpenStrongModal(true);
    setOpenKeywordId(null); // el tooltip ya cumplió su función
  };

  const closeStrong = () => {
    setOpenStrongModal(false);
    setSelectedStrongInfo(null);
  };

  if (error)
    return <p style={{ color: "red" }}>❌ Error al cargar: {error}</p>;

  if (loading)
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
        <CircularProgress color="primary" />
        <p style={{ marginTop: "1rem" }}>Cargando capítulo...</p>
      </Box>
    );

  if (!data) return null;

  const { verses } = data;

  const renderVerse = (verse) => {

    // Usamos Map (y no un objeto literal) porque las claves vienen del texto bíblico y
    // pueden colisionar con propiedades heredadas de Object.prototype
    // (ej. "constructor" en Hebreos 11:10), lo que rompía el render.
    const keywordMap = new Map(
        (verse.keywords || []).map((k) => {
          const key = ((k.translatedWord || "").trim() || "").toString();

          const normalized = {
            translatedWord: (k.translatedWord || "").trim(),
            inflectionWord: k.inflectionWord,
            transliteratedWord: k.transliteratedWord,
            strongNumber: k.strongNumber,
            sourceTransliteration: k.sourceTransliteration,
            sourceInflection: k.sourceInflection,
            sourceMeaning: k.sourceMeaning,
            color: k.color,
            compoundTransliteration: k.compoundTransliteration,
            compoundInflection: k.compoundInflection,
            compoundMeaning: k.compoundMeaning,
          };

          return [key, normalized];
        })
    );

    // indice por primera palabra para evitar chequear todas las keywords en cada posición
    const firstWordMap = new Map();
    const keywordKeys = Array.from(keywordMap.keys());
    const trimSpacesLocal = (s = "") => s.toString().trim();
    const removeTrailingPunctuationLocal = (str = "") => {
      if (!str) return str;
      const last = str.charAt(str.length - 1);
      return ",.:;!?".includes(last) ? str.slice(0, -1) : str;
    };

    keywordKeys.forEach((kw) => {
      const first = trimSpacesLocal(removeTrailingPunctuationLocal(kw.split(" ")[0] || "")).toString();
      if (!first) return;
      if (!firstWordMap.has(first)) firstWordMap.set(first, []);
      firstWordMap.get(first).push(kw);
    });

    // Set para recordar keywords que ya hicieron match en este verso (no volver a evaluarlas)
    const usedKeywords = new Set();

    const words = verse.text.split(" ");

    const trimSpaces = (s = "") => s.toString().trim();
    const removeTrailingPunctuation = (str = "") => {
      if (!str) return str;
      const last = str.charAt(str.length - 1);
      return ",.:;!?".includes(last) ? str.slice(0, -1) : str;
    };

    // Procesar texto: dividir y detectar frases
    const tokens = [];
    let i = 0;

    while (i < words.length) {
        // Si la palabra es el placeholder de salto de párrafo, insertar un salto de línea
        if (words[i] === '\\par' || words[i] === '\\\\par') {
          tokens.push(<br key={`br-${i}`} />);
          // mantener el espacio después del salto para consistencia visual
          tokens.push(" ");
          i++;
          continue;
        }

        // Intentar emparejar una palabra clave que traducida consiste de varias palabras
        // Primero obtener las keywords cuya primera palabra coincide con la palabra actual
        const candidateFirst = trimSpaces(removeTrailingPunctuation(words[i] || ""));
        const possibleKeys = firstWordMap.get(candidateFirst) || [];

        // Optimización: filtrar las posibles keys para excluir las ya usadas antes de evaluar
        const possibleUnseenKeys = possibleKeys.filter(k => !usedKeywords.has(k));

        // Buscar entre las posibles no usadas y detenerse en el primer match (evita evaluar repetidamente las mismas kw)
        let matchKeyword = null;
        for (let pkIdx = 0; pkIdx < possibleUnseenKeys.length; pkIdx++) {
          const kw = possibleUnseenKeys[pkIdx];
          const kwLen = kw.split(" ").length;
          const candidate = words.slice(i, i + kwLen).join(" ");
          const candidateClean = trimSpaces(candidate);
          const candidateNoPunct = trimSpaces(removeTrailingPunctuation(candidateClean));
          if (candidateNoPunct === kw) {
            matchKeyword = kw;
            break;
          }
        }

        if (matchKeyword) {  //palabras clave que traducidas son varias palabras

          // Marcar esta keyword como usada para no evaluarla de nuevo
          usedKeywords.add(matchKeyword);

          // Además, removerla del índice `firstWordMap` para no volver a iterar sobre ella
          try {
            const first = trimSpaces(removeTrailingPunctuation((matchKeyword || "").split(" ")[0] || ""));
            const arr = firstWordMap.get(first);
            if (arr && arr.length) {
              const idx = arr.indexOf(matchKeyword);
              if (idx !== -1) arr.splice(idx, 1);
            }
          } catch (e) {
            // safe guard: si algo falla, no rompe el rendering
          }

          const wordInfo = keywordMap.get(matchKeyword);
          const keywordsLength = matchKeyword.split(" ").length;

          // Fallbacks para mostrar en el tooltip: si no viene sourceInflection usar compoundInflection,
          // si no viene sourceTransliteration usar compoundTransliteration
          const inflectionDisplay = wordInfo.sourceInflection || wordInfo.compoundInflection || '';
          const transliterationDisplay = wordInfo.sourceTransliteration || wordInfo.compoundTransliteration || '';

          // Identificador único de esta aparición dentro del capítulo
          const keywordId = `${verse.verseNumber}-${i}`;

          // En táctil el tooltip lo controlamos nosotros (tap para abrir/cerrar);
          // en escritorio se deja el comportamiento de hover por defecto de MUI.
          const touchTooltipProps = isTouch
            ? {
                open: openKeywordId === keywordId,
                disableHoverListener: true,
                disableFocusListener: true,
                disableTouchListener: true,
              }
            : {};

          tokens.push(
            <Tooltip
              key={`kw-${i}`}
              {...touchTooltipProps}
              slotProps={{ popper: { className: KEYWORD_TOOLTIP_CLASS } }}
              title={
                <div style={{ fontSize: "1rem" }}>
                   - Morfema: <span style={{ fontSize: "1.1em", marginTop: "0px"}}>{wordInfo.transliteratedWord}</span> ({wordInfo.inflectionWord ? <em>{wordInfo.inflectionWord}</em> : null}) <br />
                   - Lexema: <span style={{ fontSize: "1.1em", marginTop: "0px", wordBreak: 'break-word'}}>{transliterationDisplay}</span> ({inflectionDisplay ? <em>{inflectionDisplay}</em> : null}) <br />
                   { /* show sourceMeaning, or compoundMeaning if sourceMeaning is null/empty */ }
                   - Significado usual: {(wordInfo.sourceMeaning || wordInfo.compoundMeaning) ? (wordInfo.sourceMeaning || wordInfo.compoundMeaning) : ''} <br />
                   <Button
                     onClick={() => openStrong(wordInfo)}
                     size="small"
                     style={{ color: '#e0f2ff', textTransform: 'none', padding: 5, minWidth: 0 }}
                   >
                     Ver detalle (Strong {wordInfo.strongNumber})
                   </Button>
                 </div>
               }
               arrow
             >
              <span
                data-strong={wordInfo.strongNumber}
                data-llave={`strong-${wordInfo.strongNumber}`}
                data-keyword-anchor=""
                onClick={() => {
                  setHoveredStrong(wordInfo.strongNumber);
                  // En táctil el tap alterna el tooltip de esta palabra
                  if (isTouch) {
                    setOpenKeywordId((prev) => (prev === keywordId ? null : keywordId));
                  }
                }}
                onMouseEnter={isTouch ? undefined : () => setHoveredStrong(wordInfo.strongNumber)}
                onMouseLeave={isTouch ? undefined : () => setHoveredStrong(null)}
                style={{
                  color: wordInfo.compoundMeaning ? '#60ba20' : "#3386d4",
                  cursor: "help",
                  // Sin el resalte azul del navegador al tocar, que ensucia el texto
                  WebkitTapHighlightColor: "transparent",
                  fontWeight: hoveredStrong === wordInfo.strongNumber ? 700 : 500,
                  textDecoration: hoveredStrong === wordInfo.strongNumber ? "underline" : "none",
                }}
              >
                   {words.slice(i, i + keywordsLength).join(" ")}
                   {wordInfo.transliteratedWord ? (
                     <sup style={{ fontSize: '0.85em', marginLeft: '0.1rem', verticalAlign: 'super', color: '#888' }}>
                       {wordInfo.transliteratedWord}
                     </sup>
                   ) : null}
              </span>
            </Tooltip>
          );

          i += keywordsLength; // saltar la serie de palabras completa

          tokens.push(" "); // espacio
          continue;

        }

        tokens.push(words[i]);

        tokens.push(" "); // espacio
        i++;
    }

    const isBlinking = blinkVerse === verse.verseNumber;

    return (
      <Box
        key={verse.verseNumber}
        ref={(el) => {
          if (el) verseRefs.current.set(verse.verseNumber, el);
          else verseRefs.current.delete(verse.verseNumber);
        }}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '0.3rem',
          marginTop: '0.3rem',
          borderRadius: '4px',
          ...(isBlinking && {
            animation: `${theme.palette.mode === 'dark' ? blinkDark : blinkLight} ${BLINK_MS}ms ease-in-out ${BLINK_COUNT}`,
          }),
        })}
      >
        <div style={{ flex: '0 0 1rem', textAlign: 'right', paddingRight: '0.4rem', lineHeight: '1.6' }}>
          <strong>{verse.verseNumber}</strong>
        </div>
        <div style={{ flex: '1 1 auto', lineHeight: '1.6', fontSize: '1.12rem' }}>
          {tokens}
        </div>
      </Box>
    );
  };


  return (
    <Fade in={!loading} timeout={500}>
      <Box>

        {/* Mostrar top 4 keywords en la parte superior */}
        {topKeywords && topKeywords.length > 0 && (() => {
            const filteredTopKeywords = topKeywords.filter(k => (k.count || 0) > 2);
            if (!filteredTopKeywords.length) return null;
            return (
              <Box mb={1}>
                <strong>Palabras clave:</strong>{' '}
                {filteredTopKeywords.map((k) => {
                   const isActive = hoveredStrong === k.strongNumber;
                   const handleActivate = () => {
                     if (isActive) {
                       // si ya está activo, simplemente quitar subrayado
                       setHoveredStrong(null);
                       return;
                     }

                     // limpiar cualquier subrayado previo
                     setHoveredStrong(null);
                     // en el siguiente tick fijar el strong seleccionado
                     setTimeout(() => setHoveredStrong(k.strongNumber), 0);
                   };

                   const handleKeyDown = (e) => {
                     if (e.key === 'Enter' || e.key === ' ') {
                       e.preventDefault();
                       handleActivate();
                     }
                   };

                   return (
                     <span
                       key={k.strongNumber}
                       onClick={handleActivate}
                       onKeyDown={handleKeyDown}
                       role="button"
                       tabIndex={0}
                       aria-pressed={isActive}
                       style={{
                         display: 'inline-block',
                         marginRight: '0.6rem',
                         padding: '0.18rem 0.45rem',
                         background: isActive ? '#c78c50' : '#dbb186',
                         borderRadius: '12px',
                         fontSize: '1rem',
                         cursor: 'pointer',
                         textDecoration: isActive ? 'underline' : 'none',
                         outline: 'none'
                       }}
                     >
                       {k.sourceMeaning} {k.sourceTransliteration ? (
                            <i><sup style={{ fontSize: '0.80em', marginLeft: '0.05rem', verticalAlign: 'super', color: '#666' }}>
                              {k.sourceTransliteration}
                            </sup></i>
                       ) : null} ({k.count})
                     </span>
                   );
                 })}
               </Box>
            );
        })()}

        {/* Mostrar top keywords en la parte superior */}
        {topCompoundKeywords && topCompoundKeywords.length > 0 && (() => {
            const filteredTopCompound = topCompoundKeywords.filter(k => (k.count || 0) > 1);
            if (!filteredTopCompound.length) return null;
            return (
              <Box mb={1}>
                <strong>Frases clave:</strong>{' '}
                {filteredTopCompound.map((k) => {
                   const isActive = hoveredStrong === k.strongNumber;
                   const handleActivate = () => {
                     if (isActive) {
                       // si ya está activo, simplemente quitar subrayado
                       setHoveredStrong(null);
                       return;
                     }

                     // limpiar cualquier subrayado previo
                     setHoveredStrong(null);
                     // en el siguiente tick fijar el strong seleccionado
                     setTimeout(() => setHoveredStrong(k.strongNumber), 0);
                   };

                   const handleKeyDown = (e) => {
                     if (e.key === 'Enter' || e.key === ' ') {
                       e.preventDefault();
                       handleActivate();
                     }
                   };

                   return (
                     <span
                       key={k.strongNumber}
                       onClick={handleActivate}
                       onKeyDown={handleKeyDown}
                       role="button"
                       tabIndex={0}
                       aria-pressed={isActive}
                       style={{
                         display: 'inline-block',
                         marginRight: '0.6rem',
                         padding: '0.18rem 0.45rem',
                         background: isActive ? '#d19b64' : '#e3b88d',
                         borderRadius: '12px',
                         fontSize: '1rem',
                         cursor: 'pointer',
                         textDecoration: isActive ? 'underline' : 'none',
                         outline: 'none'
                       }}
                     >
                       {k.compoundMeaning} {k.compoundTransliteration ? (
                                                   <i><sup style={{ fontSize: '0.80em', marginLeft: '0.05rem', verticalAlign: 'super', color: '#666' }}>
                                                     {k.compoundTransliteration}
                                                   </sup></i>
                                              ) : null} ({k.count})
                     </span>
                   );
                 })}
               </Box>
            );
        })()}



        <h2>
          {data.book} {data.chapter}
        </h2>
        {verses.map(renderVerse)}

        {/* Dialog para mostrar StrongDetail en modal */}
        <Dialog
          open={openStrongModal}
          onClose={closeStrong}
          fullWidth
          maxWidth="md"
          aria-labelledby="strong-dialog-title"
          PaperProps={{ sx: { m: 1, width: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' } }}
        >
          <DialogTitle id="strong-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
            {selectedStrongInfo ? `Strong ${selectedStrongInfo.strongNumber}` : 'Detalle Strong'}
            <IconButton onClick={closeStrong} size="small" aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ px: 1, py: 1.5 }}>
            {selectedStrongInfo ? (
              <StrongDetail
                strongCode={selectedStrongInfo.strongNumber}
                strongNumber={selectedStrongInfo.strongNumber}
                initialData={selectedStrongInfo}
                onClose={closeStrong}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
    </Fade>
  );
}
